import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  AccountPublicApiService
} from '@/app/platform/platform-core/user/services/account-public-api.service';
import { PublicAccountShellComponent } from '../public-account-shell/public-account-shell.component';

type VerifyState = 'verifying' | 'success' | 'failed' | 'missingToken';

/** Landingspagina voor e-mailverificatie via /verify?token=... */
@Component({
  selector: 'verify-page',
  imports: [FormsModule, RouterLink, TranslocoModule, SharedModule, PublicAccountShellComponent],
  templateUrl: './verify-page.component.html',
  styleUrl: './verify-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerifyPageComponent implements OnInit {
  private readonly accountApi = inject(AccountPublicApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);

  protected readonly state = signal<VerifyState>('verifying');
  protected readonly resendEmail = signal('');
  protected readonly isResending = signal(false);
  protected readonly resendCompleted = signal(false);

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.state.set('missingToken');
      return;
    }
    this.verify(token);
  }

  async onResendVerification(): Promise<void> {
    if (!this.resendEmail().trim()) {
      return;
    }
    this.isResending.set(true);
    const result = await this.accountApi.resendVerification(
      this.resendEmail().trim(),
      this.transloco.getActiveLang()
    );
    this.isResending.set(false);
    result.ifSuccess(() => this.resendCompleted.set(true));
  }

  private async verify(token: string): Promise<void> {
    const result = await this.accountApi.verify(token);
    result.ifSuccess(verified => this.state.set(verified ? 'success' : 'failed'));
    result.ifError(() => this.state.set('failed'));
  }
}
